import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { MarcaVehiculo, MarcasVehiculosPagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchMarcasVehiculos = ({
  page = 1,
  nombre = '',
  page_size = 10,
}: GetMarcasVehiculosParams) => {
  return useQuery({
    queryKey: ['marcasVehiculos', page, nombre, page_size],
    queryFn: async () => {
      return getMarcasVehiculos({ page, nombre, page_size });
    },
  });
};

export const useGetMarcaVehiculo = (id: string) => {
  return useQuery({
    queryKey: ['marcasVehiculos', id],
    queryFn: async () => {
      return getMarcaVehiculo(id);
    },
  });
};

export const useCreateMarcaVehiculo = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMarcaVehiculo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marcasVehiculos'] });
    },
    onSettled: () => {
      navigate(returnUrl, { replace: true });
    },
  });
};

export const useUpdateMarcaVehiculo = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMarcaVehiculo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marcasVehiculos'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetMarcasVehiculosParams = {
  page?: number;
  nombre?: string;
  page_size?: number;
};
export type CreateMarcaVehiculoParams = Omit<
  MarcaVehiculo,
  'id_marca_vehiculo'
>;
export type UpdateMarcaVehiculoParams = {
  id: string;
  data: CreateMarcaVehiculoParams;
};

export const getMarcasVehiculos = async (params?: GetMarcasVehiculosParams) => {
  const { page = 1, nombre = '', page_size = 10 } = params || {};

  return get<MarcasVehiculosPagingRes>(
    `${urlBackend}api/v1/marcas-vehiculos/?page=${page}&nombre=${nombre}&page_size=${page_size}`,
    true
  );
};

export const getMarcaVehiculo = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<MarcaVehiculo>(
    `${urlBackend}api/v1/marcas-vehiculos/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createMarcaVehiculo = async (data: CreateMarcaVehiculoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<MarcaVehiculo>(
    `${urlBackend}api/v1/marcas-vehiculos/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateMarcaVehiculo = async ({
  data,
  id,
}: UpdateMarcaVehiculoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<MarcaVehiculo>(
    `${urlBackend}api/v1/marcas-vehiculos/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
